import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { hivesApi } from "../services/hivesApi";
import { formatDateOnly } from "../services/hiveActions";
import { poolApi, vendorApi } from "../services/businessApi";
import { healthApi } from "../services/healthApi";
import { teamApi } from "../services/teamApi";
import { PromptDialog } from "../components/common/PromptDialog";
import { businessAnalyticsApi } from "../services/businessApi";
import { sitesApi } from "../services/sitesApi";
import { useFeatureTheme } from "../context/FeatureThemeContext";
import { useToast } from "../context/ToastContext";
import { SummaryCards } from "../components/hives/SummaryCards";
import { QuickStatsBar } from "../components/hives/QuickStatsBar";
import { HiveMap } from "../components/hives/HiveMap";
import { DataTable } from "../components/common/DataTable";
import { Card } from "../components/common/Card";
import { Button } from "../components/common/Button";
import { Badge } from "../components/common/Badge";
import { byUrgency, lastCheckedCell, nextCheckCell, urgencyTone } from "../components/hives/hiveUrgency";
import { NeedsAttentionPanel } from "../components/hives/NeedsAttentionPanel";
import { DuplicateQuestions } from "../components/duplicates/DuplicateQuestions";
import { duplicatesApi } from "../services/duplicatesApi";
import { SiteGrid } from "../components/hives/SiteGrid";
import { groupByAccount, hasMultipleAccounts } from "../components/hives/accountGrouping";
import { issuesByHive } from "../components/hives/siteRollup";
import { Spinner } from "../components/common/Spinner";
import { UpgradeBanner } from "../components/theme/UpgradeBanner";
import { HiveLimitBanner } from "../components/theme/HiveLimitBanner";
import { OtherAppCard } from "../components/common/OtherAppCard";
import { DidYouKnowTip } from "../components/theme/DidYouKnowTip";
import { accountApi } from "../services/accountApi";

export function DashboardPage() {
  const { hasFeature, theme, hasAllFeatures, canSeeFinancials, canChangeLists } = useFeatureTheme();
  const { notify } = useToast();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  // Invitations addressed to you (#312). Fetched here rather than by a
  // separate banner component, because they now rank INSIDE the
  // attention list rather than sitting beside it.
  const [invites, setInvites] = useState([]);
  // Which hive an issue is being written for, or null (#328).
  const [issueFor, setIssueFor] = useState(null);
  const [hivesFailed, setHivesFailed] = useState(false);
  const [hives, setHives] = useState([]);
  // Flagged hives first. A marker halfway down a long list is barely better
  // than no marker -- the point is to see what needs doing without reading
  // every row. Never-checked sorts above merely-overdue: a hive nobody has
  // opened yet and one left too long are different problems.
  //
  // A sorted copy, not a re-fetch: needs_attention already comes from the
  // API (V034), so ordering is presentation and belongs here rather than in
  // a second server round trip.
  // Shared with the hive list since #206. Two screens deciding
  // independently which hives are overdue is exactly the drift this
  // codebase keeps paying for.
  const hivesByUrgency = useMemo(() => byUrgency(hives), [hives]);
  const [dueHives, setDueHives] = useState([]);
  const [mapHives, setMapHives] = useState([]);
  // From the pool (V037), not the retired HoneyInventory table. The old
  // page is gone from the nav, so a figure sourced from it would freeze
  // at whatever was last typed there and look maintained (#218).
  const [pool, setPool] = useState(null);
  // Vendors holding stock who have gone quiet. Loaded separately and
  // best-effort: a business feature must never be able to stop the
  // hive dashboard rendering.
  const [quietVendors, setQuietVendors] = useState(null);
  // Open issues across the apiary (#251). Its own state because the tile
  // that claimed to count these was counting overdue hives instead.
  const [openIssues, setOpenIssues] = useState([]);
  // {hive_id: "urgent"|"watch"} -- what the schedule cannot see. A hive
  // checked yesterday with an urgent issue still needs somebody today.
  const issuesForHives = useMemo(() => issuesByHive(openIssues), [openIssues]);

  // Sites first (#282). The hive list is still one click away, but it is
  // no longer what somebody with forty hives is shown on arrival.
  const [view, setView] = useState("sites");
  const [weather, setWeather] = useState(null);
  const [triage, setTriage] = useState(null);
  // "Did this happen once or twice?" (#272). Usually empty, and the
  // panel renders nothing when it is.
  const [duplicates, setDuplicates] = useState([]);
  const [resolvingDup, setResolvingDup] = useState(null);
  // Health folded into one list instead of a second card beside it. The
  // Health Triage table showed the same hives in a different order, and a
  // dashboard with two worst-first lists makes the reader diff them. What
  // it uniquely knew -- a hive declining while still on schedule -- is now
  // one of the reasons a hive appears above.
  const healthScores = useMemo(() => {
    const map = {};
    for (const row of triage || []) map[row.hive_id] = row.score;
    return map;
  }, [triage]);
  const [activityFeed, setActivityFeed] = useState(null);
  const [costSummary, setCostSummary] = useState(null);
  /*
   * Bumped to re-run the loader below.
   *
   * submitIssue used to call load() directly, and load() is declared
   * INSIDE that effect -- so the call threw ReferenceError, the catch
   * caught it, and the user who had just successfully logged an issue was
   * told "Could not log the issue". The issue was on the server; only the
   * refresh and the message were wrong.
   *
   * A counter rather than hoisting load() out: the effect closes over a
   * `cancelled` flag and seven settled promises, and lifting it into a
   * useCallback to fix a refresh would be a large change to careful code
   * (#242's spinner-forever fix lives in there) for no gain.
   */
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      /*
       * allSettled and a finally, NOT a bare Promise.all (#242).
       *
       * This was `await Promise.all([...])` with no catch. poolApi.get()
       * 403s on any account below the tier that includes the honey chain,
       * Promise.all rejects on the first rejection, the await threw, and
       * setLoading(false) below was never reached -- so the dashboard, the
       * landing page, sat on its spinner forever for every Free-tier user.
       * Nothing was broken except the error handling.
       *
       * #217 wrote this rule after the same failure elsewhere: allSettled
       * plus an explicit state per block, never one rejection taking the
       * page. The `finally` is the belt to that braces -- whatever happens
       * above, the spinner ends.
       */
      // HoneyPool since #247, not HoneyChain: GET /honey/pool moved to the
      // Free-tier flag with the rest of the pool, so gating this on
      // bottling would hide the balance from the accounts that now own one.
      const gated = hasFeature("HoneyPool");
      const [hivesRes, dueRes, mapRes, poolRes, issuesRes, dupRes, sharesRes] = await Promise.allSettled([
        hivesApi.list({ status: "Active" }),
        hivesApi.due(),
        hivesApi.map(),
        // Only asked for where it can be answered, matching how triage,
        // the activity feed and the P&L below are already gated -- and the
        // sidebar, which hides this page behind the same flag. Asking for
        // it ungated was what produced the 403 in the first place.
        gated ? poolApi.get() : Promise.resolve(null),
        // Ungated: noticing something wrong with a hive is core to keeping
        // bees, so the endpoint is open to every tier and so is this.
        hivesApi.openIssues(),
        // Ungated too: this protects a treatment record, and that should
        // not be something an account can find itself without (#272).
        duplicatesApi.list(),
        // Invitations addressed to you (#312). Ungated and cheap, and the
        // one row here that may be the ONLY thing a brand-new invitee
        // has -- they have no hives of their own to list yet.
        accountApi.listShares(),
      ]);
      if (cancelled) return;

      // A failed hive list is NOT an empty apiary. Leaving it as [] would
      // tell someone with forty hives that they have none, which is the
      // #197 mistake; the banner below says we could not check.
      setHivesFailed(hivesRes.status === "rejected");
      setHives(hivesRes.status === "fulfilled" ? hivesRes.value.hives : []);
      setDueHives(dueRes.status === "fulfilled" ? dueRes.value.due_hives : []);
      setMapHives(mapRes.status === "fulfilled" ? mapRes.value.hives : []);
      // null renders as "--" rather than a confident 0 lbs.
      setPool(poolRes.status === "fulfilled" ? poolRes.value : null);
      setOpenIssues(issuesRes.status === "fulfilled" ? issuesRes.value.issues : []);
      // [] on failure, like duplicates: not knowing about an invitation is
      // not the same as claiming there is none, but the panel simply omits
      // the row rather than asserting anything either way.
      setInvites(sharesRes.status === "fulfilled"
        ? (sharesRes.value.received || []).filter((sh) => sh.status === "Pending")
        : []);
      // [] on failure is right here and wrong for hives: an empty list
      // means "no questions", which is the normal state, and the panel
      // renders nothing at all rather than claiming anything.
      setDuplicates(dupRes.status === "fulfilled" ? dupRes.value.suspicions : []);
      const mapHivesLoaded = mapRes.status === "fulfilled" ? mapRes.value.hives : [];

      // Weather through the API, not a second provider (#273/#282).
      // web/src/services/weatherApi.js used to call Open-Meteo straight
      // from the browser, which meant the dashboard and the hive detail
      // screen could show different temperatures for the same yard on the
      // same afternoon. One source, server-side, stored -- which is also
      // the only version app.usp_CalculateHealthScore can read.
      const firstSiteId = mapHivesLoaded[0]?.site_id;
      if (firstSiteId) {
        sitesApi.weather(firstSiteId)
          .then((r) => {
            if (cancelled || !r?.weather || r.weather.temperature_c === null) return;
            setWeather({
              temperatureF: (r.weather.temperature_c * 9) / 5 + 32,
              summary: r.weather.condition || "Current conditions",
            });
          })
          .catch(() => {});
      }
      if (hasFeature("HealthScoring")) {
        healthApi.triage().then((r) => !cancelled && setTriage(r.hives.slice(0, 5))).catch(() => {});
      }
      /* BOTH FLAGS, because the endpoint requires both (3 Oct).
         
         GET /team/activity-feed is gated
         `feature=("TeamCollaboration", "team_activity_feed")`, and
         secure_endpoint's tuple means ALL -- it loops over the names and
         any one of them refuses. This checked only the first, so a Free
         account passed here and collected a 403: V113 (#445) gave Free
         TeamCollaboration because it is sold two seats, while every
         team_* sub-flag stayed at Beekeeper.
         
         Reported as console errors on sign-out with no visible breakage,
         which is exactly how long this could have lasted. */
      if (hasAllFeatures("TeamCollaboration", "team_activity_feed")) {
        teamApi.activityFeed().then((r) => !cancelled && setActivityFeed(r.activity.slice(0, 5))).catch(() => {});
      }
      /* GATED AT ALL, which it was not.
         
         Every other call in this function asks hasFeature first; this one
         went out unconditionally, and GET /honey/vendors/needs-chasing is
         HoneyBusiness. So every account below Business 403'd on it on
         every dashboard load -- the second console error reported, and a
         wasted round trip on a cold-start-sensitive plan (#374).
         
         The catch kept the page working, which is why it went unnoticed:
         a handled refusal looks identical to no vendors needing chasing. */
      if (hasFeature("HoneyBusiness")) {
        vendorApi.needsChasing()
          .then((r) => !cancelled && setQuietVendors(r))
          .catch(() => !cancelled && setQuietVendors({ vendors: [] }));
      }
      /* NOT when money is hidden from this person on the account they are
         working in (#534): the server answers 403 and the console fills
         with refusals the page already knew were coming. */
      if (hasFeature("BusinessAdvanced") && canSeeFinancials !== false) {
        businessAnalyticsApi.pnl(new Date().getFullYear()).then((r) => !cancelled && setCostSummary(r.pnl)).catch(() => {});
      }
    }
    load()
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [hasFeature, hasAllFeatures, canSeeFinancials, reloadKey]);

  /*
   * Yours versus theirs (#295).
   *
   * is_my_account is decided by the SERVER, which already knows who is
   * asking -- the same field accountGrouping.js uses for the hive list,
   * so the tiles and the sections below cannot disagree about whose a
   * hive is.
   *
   * A hive with no account at all counts as YOURS: that is an older
   * response or one the API could not attribute, and a hive you can see
   * is far likelier to be yours than a stranger's. Counting it as
   * somebody else's would understate your own total, which is the
   * direction that matters.
   */
  const isMine = (h) => h.is_my_account || h.account_user_id == null;
  const mine = hives.filter(isMine);
  const helping = hives.filter((h) => !isMine(h));
  const mineDue = dueHives.filter(isMine);
  const helpingDue = dueHives.filter((h) => !isMine(h));

  async function handleResolveDuplicate(suspicionId, answer, duplicateLogIds) {
    setResolvingDup(suspicionId);
    try {
      const r = await duplicatesApi.resolve(suspicionId, answer, duplicateLogIds);
      // Says what actually happened to the records rather than "Done".
      // The whole promise of this feature is that nothing was deleted,
      // so the confirmation is the place to prove it.
      if (r.status === "SameEvent") {
        // Says how many, because with three entries the user has just
        // made a choice about a subset and "the other entry" would be
        // wrong as well as vague.
        notify(
          (r.superseded || []).length === 1
            ? "Recorded as one event. The other entry is kept, marked as a duplicate."
            : `Recorded as one event. ${(r.superseded || []).length} entries are kept, marked as duplicates.`,
          "success",
        );
      } else if (r.status === "BothReal") {
        notify(
          r.incident_task_id
            ? "Both entries kept. Raised for someone to check the withdrawal period."
            : "Both entries kept.",
          "success",
        );
      } else {
        notify("Left both entries alone.", "success");
      }
      setDuplicates((prev) => prev.filter((d) => d.suspicion_id !== suspicionId));
    } catch (err) {
      notify(err.apiMessage || "Couldn't record that", "danger");
    } finally {
      setResolvingDup(null);
    }
  }

  async function handleMarkChecked(hiveId) {
    try {
      await hivesApi.logAction(hiveId, { action_type: "HealthCheck" });
      notify("Marked checked", "success");
      setDueHives((prev) => prev.filter((h) => h.hive_id !== hiveId));
    } catch (err) {
      notify(err.apiMessage, "danger");
    }
  }

  /*
   * Logging an issue now asks what is wrong (#251).
   *
   * It used to post `{action_type: "IssueFound"}` with no details at all,
   * so every issue ever logged from this page was identical to every other
   * one -- nothing to read, nothing to sort by, and no way to tell which
   * one you had just dealt with. The prompt is one line, because the
   * alternative to a quick prompt is a quick button that records nothing.
   */
  async function handleLogIssue(hiveId) {
    // PromptDialog, not window.prompt (#328). This creates a real record,
    // and a browser prompt can be suppressed by the user -- it then
    // returns null silently and logging an issue just stops working with
    // nothing on screen to say why.
    setIssueFor(hiveId);
  }

  async function submitIssue(summary) {
    const hiveId = issueFor;
    // Cancelled, or entered nothing. Not an error -- they changed their
    // mind, and a toast for that is noise.
    if (!hiveId || !summary) return setIssueFor(null);
    try {
      await hivesApi.openIssue(hiveId, { summary });
      notify("Issue logged", "warning");
      setIssueFor(null);
      setReloadKey((k) => k + 1);
    } catch (err) {
      notify(err.apiMessage || "Could not log the issue", "danger");
    }
  }

  if (loading) return <Spinner label="Loading dashboard" />;

  // Was `dueHives.length` -- the number of hives DUE FOR INSPECTION,
  // sitting directly beside a tile already showing that same number. The
  // Issues tile has never once counted issues (#251).
  const issueCount = openIssues.length;
  // Bulk in the tank plus jars on the shelf -- everything still in the
  // beekeeper's hands. Honey out with a vendor is deliberately NOT counted:
  // it is physically gone and appears as money owed on the Vendors page,
  // so including it here would count the same honey twice.
  const poolPounds = pool ? Number(pool.pool_pounds || 0) : 0;
  const bottledJars = (pool?.bottled_by_size || []).reduce((sum, b) => sum + Number(b.bottles || 0), 0);

  /*
   * The dashboard's list view, defined once and rendered whole or per
   * account (#286). groupByAccount preserves the order it is given, so
   * passing hivesByUrgency keeps worst-first inside each section.
   */
  function DashHiveTable({ rows }) {
    return (
          <DataTable
            rows={rows}
            rowKey="hive_id"
            columns={[
              { key: "site_name", header: "Site", render: (r) => r.site_name || "--" },
              { key: "hive_number", header: "Hive #" },
              { key: "status", header: "Status", render: (r) => <Badge tone={r.status === "Active" ? "success" : "neutral"}>{r.status}</Badge> },
              { key: "last_checked", header: "Last Check", render: lastCheckedCell },
              // next_check_due and needs_attention are both computed by the
              // API from the account's inspection interval (V034), so this
              // table, the hive list, the briefing and the voice assistant
              // cannot disagree about when a hive is due.
              { key: "next_check_due", header: "Next Check", render: nextCheckCell },
                            {
                key: "actions",
                header: "Actions",
                render: (r) => (
                  <div style={{ display: "flex", gap: 6 }} onClick={(e) => e.stopPropagation()}>
                    <Button size="sm" variant="secondary" onClick={() => handleMarkChecked(r.hive_id)}>Mark Checked</Button>
                    <Button size="sm" variant="secondary" onClick={() => handleLogIssue(r.hive_id)}>Log Issue</Button>
                  </div>
                ),
              },
            ]}
            rowTone={urgencyTone}
            onRowClick={(r) => navigate(`/hives/${r.hive_id}`)}
            emptyTitle="No hives yet"
            emptyDescription="Add your first hive to get started."
            emptyAction={canChangeLists === false ? null : <Button onClick={() => navigate("/hives/new")}>Add Hive</Button>}
          />
    );
  }

  return (
    <div>
      <UpgradeBanner />
      <HiveLimitBanner />

      {/* The other half of the app, at most twice in an account's life
          (#302). Below the banners and above the work: it is a
          suggestion, so it must not sit where the urgent things go, and
          it renders nothing at all unless the server says there is
          something to say. */}
      <OtherAppCard />


      {/* "We could not check" is a different statement from "you have no
          hives", and only one of them is safe to imply. Before #242 a
          failed list silently rendered as an empty apiary. */}
      {hivesFailed && (
        <Card className="section-spacing">
          <p style={{ margin: 0 }}>
            Couldn't load your hives just now, so the counts below are
            incomplete. Everything you've logged is still there — refresh
            when you have a connection.
          </p>
        </Card>
      )}

      {/* WHOSE hives, and whose are due (#295).
          On an account that helps at somebody else's apiary these two
          were silently the sum of yours and theirs. "Due Today" is the
          worse of the pair: it is a number somebody ACTS on, and acting
          on a total that includes another account's overdue hives sends
          them to the wrong yard.

          The big number is YOURS -- the one read at a glance -- and the
          rest goes on a quiet second line. No sixth tile: five is
          already the width of the row, and the split is absent entirely
          for anybody not on a team, which is most accounts.

          Honey and Bottled are NOT split. Testing on 27 Aug confirmed
          they are already scoped to the account being viewed, and they
          should be: somebody else's honey is their stock, not a share
          you own. */}
      <SummaryCards
        cards={[
          {
            label: "Total Hives",
            value: mine.length,
            sub: helping.length ? `+${helping.length} you help with` : undefined,
          },
          {
            label: "Hives Due Today",
            value: mineDue.length,
            tone: mineDue.length > 0 ? "warning" : undefined,
            sub: helpingDue.length ? `+${helpingDue.length} you help with` : undefined,
          },
          { label: "Issues", value: issueCount, tone: issueCount > 0 ? "danger" : undefined },
          // Two figures rather than one total: bulk and jars are not
          // interchangeable, and "96 lbs" hides whether any of it is
          // ready to sell.
          { label: "Honey in the Pool", value: pool === null ? "--" : `${poolPounds.toFixed(0)} lbs`, tone: "success" },
          { label: "Bottled", value: pool === null ? "--" : `${bottledJars} jars`, tone: "success" },
        ]}
      />

      <QuickStatsBar tasksToday={dueHives.length} weather={weather} />


      {/* Vendors who have gone quiet (#215 follow-on). Renders NOTHING
          when there is nothing to chase -- a hobbyist with no vendors
          should not see an empty panel explaining a feature they do not
          use, and someone whose vendors are all current has nothing to
          act on. */}
      {quietVendors?.vendors?.length > 0 && (
        <Card title="🏪 Outlets to chase" className="section-spacing">
          <p style={{ color: "var(--color-text-muted)", marginTop: 0 }}>
            Holding stock with no delivery or payment in the last {quietVendors.silence_days} days.
          </p>
          <DataTable
            rows={quietVendors.vendors}
            rowKey="client_id"
            columns={[
              { key: "client_name", header: "Outlet" },
              {
                key: "holding",
                header: "Holding",
                render: (r) => [
                  Number(r.bottles) > 0 ? `${r.bottles} bottles` : null,
                  Number(r.bulk_pounds) > 0 ? `${r.bulk_pounds} lb bulk` : null,
                ].filter(Boolean).join(" + "),
              },
              { key: "last_delivery", header: "Last delivery", render: (r) => formatDateOnly(r.last_delivery) },
              {
                key: "last_payment",
                header: "Last payment",
                // "Never" is the state most worth seeing, so it is spelled
                // out rather than left as an empty cell.
                render: (r) => (r.last_payment ? formatDateOnly(r.last_payment) : <Badge tone="warning">Never</Badge>),
              },
              { key: "days_quiet", header: "Quiet for", render: (r) => `${r.days_quiet} days` },
            ]}
            onRowClick={() => navigate("/business/vendors")}
          />
        </Card>
      )}

      {theme === "Basic" && (
        <DidYouKnowTip tipId="dashboard-health-triage">
          Upgrade to the Advanced theme to see a Health Triage card that sorts your hives worst-first, so problems never hide at the bottom of the list.
        </DidYouKnowTip>
      )}

      {/* Layer one (#282): what needs a person, capped so it is the same
          size at three hives and at three hundred. Above everything
          structural, because it is the answer rather than the index. */}
      {/* Above the hive list: a duplicate dose is a question only a
          person can answer, and it stops being answerable the longer it
          waits for somebody to remember the day (#272). */}
      <DuplicateQuestions
        suspicions={duplicates}
        busyId={resolvingDup}
        onResolve={handleResolveDuplicate}
      />

      <NeedsAttentionPanel
        hives={hives}
        invites={invites}
        issuesByHive={issuesForHives}
        healthByHive={healthScores}
        onOpen={(hiveId) => navigate(`/hives/${hiveId}`)}
        onMarkChecked={handleMarkChecked}
      />

      <Card
        /* Not "Your Hives" when some of them are not. The whole
           complaint in #286 was the app being vague about whose is
           whose; a heading that asserts it is worse than one that does
           not mention it. */
        title={hasMultipleAccounts(hives) ? "Hives" : "Your Hives"}
        actions={
          <div style={{ display: "flex", gap: 8 }}>
            <Button size="sm" variant={view === "sites" ? "primary" : "secondary"} onClick={() => setView("sites")}>Sites</Button>
            <Button size="sm" variant={view === "list" ? "primary" : "secondary"} onClick={() => setView("list")}>List</Button>
            <Button size="sm" variant={view === "map" ? "primary" : "secondary"} onClick={() => setView("map")}>Map</Button>
          </div>
        }
      >
        {view === "sites" ? (
          <SiteGrid
            hives={hives}
            openIssues={openIssues}
            onOpenSite={(site) => navigate(site.site_id ? `/hives?site_id=${site.site_id}` : "/hives")}
          />
        ) : view === "list" ? (
          /* Grouped only when there is more than one account to tell
             apart -- see accountGrouping.js. A solo beekeeper sees the
             list exactly as before. */
          hasMultipleAccounts(hives) ? (
            groupByAccount(hivesByUrgency).map((group) => (
              <div key={group.account_user_id ?? "mine"} style={{ marginBottom: "var(--space-4)" }}>
                <h3 style={{ fontSize: "var(--font-size-sm)", textTransform: "uppercase",
                             letterSpacing: ".06em", color: "var(--color-text-muted)",
                             margin: "0 0 var(--space-2)" }}>
                  {group.label}
                </h3>
                <DashHiveTable rows={group.hives} />
              </div>
            ))
          ) : (
            <DashHiveTable rows={hivesByUrgency} />
          )
        ) : (
          <HiveMap
            hives={mapHives}
            weatherOverlay={theme !== "Basic" && weather && mapHives[0] ? { lat: mapHives[0].latitude, lon: mapHives[0].longitude, summary: `${weather.summary}, ${Math.round(weather.temperatureF)}°F` } : null}
            onMarkChecked={handleMarkChecked}
            onLogIssue={handleLogIssue}
          />
        )}
      </Card>

      {/* Reported 27 Aug: these were touching the hive card above. */}
      <div style={{ height: "var(--space-5)" }} />

      {/* Below the hives, deliberately (reported 25 Aug). Both of these
          are REVIEW -- money already spent, work already done -- and the
          top of the dashboard is for what needs a person now. Greeting
          somebody with last year's costs before "needs you now" inverts
          the whole point of the #282 layering. */}
      {hasFeature("BusinessAdvanced") && costSummary && (
        <Card title="💰 Cost Summary (this year)" className="section-spacing">
          {costSummary.map((row) => (
            <div key={`${row.year}-${row.season}`} style={{ display: "flex", justifyContent: "space-between" }}>
              <span>{row.season}</span>
              <span>Income ${row.income.toFixed(0)} · Expenses ${row.expenses.toFixed(0)} · Profit ${row.profit.toFixed(0)}</span>
            </div>
          ))}
        </Card>
      )}

      {hasFeature("TeamCollaboration") && activityFeed && (
        <Card title="👥 Team Activity" className="section-spacing">
          <ul>
            {activityFeed.map((a) => (
              <li key={a.feed_id}>
                {a.performed_by ? `${a.performed_by} ` : ""}
                {a.action} — {new Date(a.timestamp).toLocaleString()}
              </li>
            ))}
          </ul>
        </Card>
      )}
      <PromptDialog
        open={issueFor !== null}
        onClose={() => setIssueFor(null)}
        onSubmit={submitIssue}
        title="Log an issue"
        label="What's wrong with this hive?"
        hint="One line. You can add detail on the hive itself later."
        // "Save issue", not "Log issue": the row button that OPENS this
        // says "Log Issue", and two controls with the same words is a
        // coin toss for anyone moving by keyboard or hearing them read
        // out (docs/UI_ACTIONS.md rule 4). Found by a test that could not
        // tell them apart either.
        confirmLabel="Save issue"
        maxLength={200}
      />

    </div>
  );
}
